import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target,
  Layers,
  Globe2,
  Users,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { STATS, COMPANY } from "@/lib/siteData";

const differentiators = [
  {
    icon: Layers,
    title: "End-to-End Capabilities",
    body: "One partner supporting every stage of your research project.",
  },
  {
    icon: Globe2,
    title: "Global Reach",
    body: "Multi-country execution capabilities across key international markets.",
  },
  {
    icon: Users,
    title: "Specialized Audiences",
    body: "Experience recruiting healthcare professionals, executives, decision-makers, and niche respondent groups.",
  },
  {
    icon: ShieldCheck,
    title: "Quality-Driven Culture",
    body: "Every project is governed by structured QA processes designed to maximize reliability and confidence.",
  },
];

const commitments = [
  "Deliver projects faster",
  "Improve data quality",
  "Reduce operational complexity",
  "Access hard-to-reach audiences",
  "Scale research operations confidently",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="vd-eyebrow">About {COMPANY.name}</p>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900">
              Enabling better research through{" "}
              <span className="text-teal-700">reliable execution</span>.
            </h1>
            <p className="mt-6 max-w-3xl text-base md:text-lg text-slate-600 leading-relaxed">
              VeriData Global is a specialized research execution partner
              supporting market research agencies, consulting firms, corporate
              insights teams, and research organizations worldwide. We believe
              exceptional research begins with exceptional execution.
            </p>
          </div>
          <div className="lg:col-span-4 lg:border-l lg:border-slate-200 lg:pl-10">
            <p className="vd-eyebrow">Our Mission</p>
            <p className="mt-4 text-slate-800 text-lg font-display leading-snug">
              To help organizations deliver high-quality research through
              reliable fieldwork, efficient operations, and uncompromising
              quality standards.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="vd-eyebrow">Who We Are</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              Built for the new complexity of global research.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
            <p>
              Research today is more complex than ever. Projects span multiple
              countries, audiences are increasingly difficult to reach, and
              clients expect faster delivery without sacrificing quality.
              VeriData Global was built to address these challenges.
            </p>
            <p>
              We provide integrated support across the research lifecycle,
              enabling our partners to focus on strategic insights while we
              manage execution. From questionnaire programming and respondent
              recruitment to data processing and reporting, we deliver the
              operational excellence that powers successful research.
            </p>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b border-slate-200 bg-slate-50/60">
        <div className="vd-container py-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white px-6 py-8 text-center md:text-left">
              <div className="font-display text-3xl md:text-4xl font-semibold text-slate-900">
                {s.value}
              </div>
              <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-slate-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="vd-eyebrow">What Makes Us Different</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              An execution philosophy built into every project.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {differentiators.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white p-8 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-xs text-slate-400">
                    0{i + 1}
                  </span>
                  <span className="h-px w-10 bg-slate-200" />
                </div>
                <d.icon className="h-6 w-6 text-teal-700 mt-6" />
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                  {d.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{d.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION + COMMITMENT */}
      <section className="bg-[#0b192c] text-white relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 vd-grid-bg-dark opacity-60 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
              Our Vision
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              To become the preferred global research execution partner for
              organizations seeking speed, quality, scalability, and trust.
            </h2>
            <div className="mt-10 flex items-center gap-3 text-teal-400 text-sm">
              <Target className="h-5 w-5" />
              Trusted · Timely · Thorough
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
              Our Commitment
            </p>
            <p className="mt-4 text-white/90 text-lg font-display">
              We are committed to helping our clients:
            </p>
            <div className="mt-8 space-y-3">
              {commitments.map((c) => (
                <div
                  key={c}
                  className="flex items-center gap-3 border border-white/10 rounded-md px-5 py-4 bg-white/5"
                >
                  <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0" />
                  <span className="text-white/90">{c}</span>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="mt-10 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm"
            >
              Start a conversation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
